/**
 * @file ActionCell.tsx
 * @description Standardized table action-cell component.
 * Renders a set of icon buttons (View, Edit, Delete) and stops
 * click propagation so row-click handlers don't fire.
 */

import * as React from 'react';
import { Eye, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

export type ActionItem = {
  /** Tooltip label */
  label: string;
  /** Click handler — receives the MouseEvent so you can stopPropagation if needed */
  onClick: (e: React.MouseEvent) => void;
  /** Icon node to render */
  icon: React.ReactNode;
  /** Additional class names for the button */
  className?: string;
  /** Whether the action is disabled */
  isDisabled?: boolean;
};

interface ActionCellProps {
  /** Show the view (Eye) button */
  onView?: (e: React.MouseEvent) => void;
  /** Show the edit (Pencil) button */
  onEdit?: (e: React.MouseEvent) => void;
  /** Show the delete (Trash) button */
  onDelete?: (e: React.MouseEvent) => void;
  /** Completely custom set of actions (overrides onView/onEdit/onDelete) */
  actions?: ActionItem[];
  /** Extra actions to show alongside the default ones */
  extraActions?: ActionItem[];
  className?: string;
}

const stopProp = (handler: ((e: React.MouseEvent) => void) | undefined) =>
  handler
    ? (e: React.MouseEvent) => {
        e.stopPropagation();
        handler(e);
      }
    : undefined;

/**
 * ActionCell – place inside a TanStack table `cell` renderer.
 *
 * @example
 * cell: ({ row }) => (
 *   <ActionCell
 *     onView={() => navigate(ROUTES.DETAIL.replace(':id', row.original.id))}
 *     onEdit={() => navigate(ROUTES.EDIT.replace(':id', row.original.id))}
 *     onDelete={() => setDeleteId(row.original.id)}
 *   />
 * )
 */
export const ActionCell = React.memo(function ActionCell({
  onView,
  onEdit,
  onDelete,
  actions,
  extraActions = [],
  className,
}: ActionCellProps) {
  const defaultActions: ActionItem[] = [
    ...(onView
      ? [
          {
            label: 'View',
            onClick: stopProp(onView)!,
            icon: <Eye className="h-4 w-4" />,
          },
        ]
      : []),
    ...(onEdit
      ? [
          {
            label: 'Edit',
            onClick: stopProp(onEdit)!,
            icon: <Pencil className="h-4 w-4" />,
          },
        ]
      : []),
    ...(onDelete
      ? [
          {
            label: 'Delete',
            onClick: stopProp(onDelete)!,
            icon: <Trash2 className="h-4 w-4" />,
            className: 'text-destructive hover:text-destructive hover:bg-destructive/10',
          },
        ]
      : []),
    ...extraActions,
  ];

  const items = actions ?? defaultActions;

  return (
    <TooltipProvider delayDuration={300}>
      <div
        className={cn('flex items-center justify-end gap-1', className)}
        onClick={(e) => e.stopPropagation()}
      >
        {items.map((action) => (
          <Tooltip key={action.label}>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className={cn('h-8 w-8', action.className)}
                onClick={action.onClick}
                disabled={action.isDisabled}
              >
                {action.icon}
                <span className="sr-only">{action.label}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p>{action.label}</p>
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
});
